import { useState } from 'react';

import { Link } from 'react-router';

import { useAppDispatch } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { LazyImg } from 'Components/LazyImg';
import { Typography } from 'Components/Typography';

import currencies from 'constants/currencies';

import { formatPriceWithCurrency } from 'utils/currencies';

import * as trackingActions from 'dux/tracking/actions';

const StyledLink = styled(Link)<{ noCTA: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  max-width: 280px;
  padding-bottom: ${({ noCTA }) => (noCTA ? 0 : theme.spacing['10x'])};

  color: inherit;

  ${legacyTheme.breakpoints.match('sm')} {
    width: 100%;
    max-width: 450px;
    padding: 0;
    padding-bottom: ${theme.spacing['10x']};

    &:last-child {
      padding-bottom: ${({ noCTA }) => (noCTA ? 0 : theme.spacing['10x'])};
    }
  }
`;

const StyledLabel = styled(Typography)`
  position: relative;

  & > p {
    position: absolute;
    top: -4px;
    left: -33px;
  }
  ${legacyTheme.breakpoints.match('sm')} {
    position: relative;
    top: auto;
    left: auto;
    text-align: center;
  }
`;

const StyledTag = styled(Typography)`
  background: white;
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-block;
  height: 36px;
  line-height: 26px !important;
  padding: ${theme.spacing['2x']};
`;

const ImageContainer = styled.div`
  position: relative;
  width: 250px;

  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  justify-items: end;

  margin-top: ${theme.spacing['8x']};
  margin-bottom: ${theme.spacing['6x']};
  ${legacyTheme.breakpoints.match('sm')} {
    width: auto;
    max-width: 250px;
    height: auto;
    margin-top: ${theme.spacing['6x']};
  }
`;

const Image = styled(LazyImg)`
  grid-area: 1 / -1;
`;

type pictureType = {
  src: string;
};

type ProductsItemProps = {
  bgLabelledPicture: pictureType;
  bgPicture: pictureType;
  capacity: string;
  capacityVariant: string;
  cardTag: string | null;
  currency: string;
  dataClick: string;
  dataFrom: string;
  displayCTA: boolean;
  label: string;
  labelVariant: string;
  price: string;
  productPageUrl: string;
  slug: string;
};

export const ProductsItem = ({
  bgLabelledPicture,
  bgPicture,
  capacity,
  capacityVariant = 'p2',
  cardTag,
  currency = currencies.USD,
  dataClick = 'see-product',
  dataFrom = 'products-block',
  displayCTA = false,
  label,
  labelVariant = 'h3',
  price,
  productPageUrl,
  slug,
}: ProductsItemProps) => {
  const [isHovered, setHovered] = useState(false);
  const dispatch = useAppDispatch();

  return (
    <StyledLink
      data-click={`${dataClick}-${slug}`}
      data-from={dataFrom}
      data-testid={`product-item-card-${slug}`}
      noCTA={!displayCTA}
      onMouseEnter={() => {
        setHovered(true);
        dispatch(trackingActions.trackHover({ dataFrom, dataClick, productSlug: slug }));
      }}
      onMouseLeave={() => setHovered(false)}
      to={productPageUrl}
    >
      <ImageContainer>
        <Image alt={label} src={(isHovered ? bgLabelledPicture : bgPicture).src} width="100%" />
      </ImageContainer>
      {/* @ts-expect-error Typography no migrated */}
      <StyledLabel markupName="h3" paragraph variant={labelVariant}>
        {label}
      </StyledLabel>
      {/* @ts-expect-error Typography no migrated */}
      <Typography color="noir" variant={capacityVariant}>
        From {formatPriceWithCurrency({ price, currency })}, {capacity}
      </Typography>

      {/* @ts-expect-error Typography no migrated */}
      {cardTag && <StyledTag>{content}</StyledTag>}
    </StyledLink>
  );
};
